import posthog from "posthog-js";

/**
 * The database stores occupation as a token (`remote_worker`), but the PostHog
 * `user_type` person property has always held the display string
 * ("Remote Worker"). Map at send time so historical and new values match in
 * PostHog; unknown tokens pass through unmapped.
 */
export const OCCUPATION_LABELS: Record<string, string> = {
  student: "Student",
  remote_worker: "Remote Worker",
  freelancer: "Freelancer",
  business_owner: "Business Owner",
  hybrid_worker: "Hybrid Worker",
  founder: "Founder",
  cafe_lover: "Café Lover",
};

export function occupationLabel(occupation?: string | null): string | null {
  if (!occupation) return null;
  return OCCUPATION_LABELS[occupation] ?? occupation;
}

/**
 * Push the user's segmentation traits to PostHog as person properties so
 * retention/funnels can be broken down by persona, age, and cafe frequency.
 *
 * Why this exists: `occupation` (and age/frequency) are written to
 * `user_preferences` DURING onboarding, but the `posthog.identify()` in
 * `useAuth` runs at SIGN-IN — before those values exist — so the person property
 * stayed empty for the ~93% of users who never sign in again. Call this wherever
 * those prefs are persisted so the person object is updated immediately.
 *
 * Only sends keys that are actually present; never overwrites with null/empty.
 * Keep `user_type` in sync with the property name set in `useAuth`'s identify().
 */
export function setUserSegmentProperties(traits: {
  occupation?: string | null;
  ageRange?: string | null;
  cafeFrequency?: string | null;
}) {
  const props: Record<string, string> = {};
  const userType = occupationLabel(traits.occupation);
  if (userType) props.user_type = userType;
  if (traits.ageRange) props.age_range = traits.ageRange;
  if (traits.cafeFrequency) props.cafe_frequency = traits.cafeFrequency;

  if (Object.keys(props).length === 0) return;
  posthog.setPersonProperties(props);
}
